import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { StepType, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { getTaskSettingsDefaults } from 'components/TasksV2/TaskCreation/model/taskSettingsDefaults'
import { getScheduleValues } from 'components/TasksV2/TaskCreation/model/settings'
import { AIStepType, useAILoadingContext } from '../AIFormStepContext'
import { JobFrequencyType } from 'components/AdminConsole/Jobs/utils'

export function useAiSettingsForm() {
  const { formData, getFormData, updateFormData, setCurrentFormSubmit, setStepStack } = useFormStepContext()
  const { generateFromPrompt } = useAILoadingContext()
  const uid = useSelector((state: RootState) => state.auth.uid)
  const defaults = getTaskSettingsDefaults(formData, false, uid)
  const savedOffset = (formData.TASK_SETTINGS as any)?.ai_schedule?.offsetMinutes ?? 15
  const [offsetMinutes, setOffsetMinutes] = useState(savedOffset)
  const methods = useForm<any>({
    defaultValues: {
      frequency_time: '',
      monthly_range: [],
      is_recurring: false,
      days_of_week: null,
      recurrence_frequency: null,
      ...defaults,
      ...formData.TIME_FRAME_SELECTION,
      type: keys.TASK_V2_TYPES.AI_TASK,
      view: keys.DEFAULT_TASK_VIEWS.AI_TASKS,
      send_action_pop: (defaults as any).send_action_pop ?? true,
    },
    mode: 'onChange',
  })
  const values = methods.watch({ nest: true })
  const latest = useRef(values)
  latest.current = { ...values, ai_schedule: { recurrence_frequency: JobFrequencyType.ONCE, offsetMinutes } }
  const submitting = useRef(false)
  const duration = values.from_ts && values.deadline ? dayjs(values.deadline).diff(dayjs(values.from_ts), 'minute') : 0
  const maxOffsetMinutes = duration > 0 ? duration : null

  const save = () => {
    updateFormData(StepType.TASK_SETTINGS, latest.current)
    updateFormData(StepType.TIME_FRAME_SELECTION, getScheduleValues(latest.current))
  }

  useEffect(() => {
    if (maxOffsetMinutes != null && offsetMinutes > maxOffsetMinutes) setOffsetMinutes(maxOffsetMinutes)
  }, [maxOffsetMinutes, offsetMinutes])

  useEffect(() => {
    save()
  }, [JSON.stringify(values), offsetMinutes]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentFormSubmit(() =>
      methods.handleSubmit(() => {
        if (submitting.current) return
        submitting.current = true
        save()
        const prompt = (getFormData?.() || (formData as any)).AI_PROMPT
        void generateFromPrompt(prompt?.prompt || '', prompt?.reference_images || [])
        setStepStack((previous) => [...previous, AIStepType.AI_SUBTASK])
      })
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { methods, values, offsetMinutes, maxOffsetMinutes, setOffsetMinutes }
}
